import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HeartHandshake, Package, ShieldCheck, GraduationCap, Users, Flame, ArrowRight, Dog, Award } from "lucide-react";
import { api } from "@/lib/api";
import { MEDIA, TAGLINE, CAMPAIGN_TAGLINE, SECONDARY } from "@/lib/media";
import { useCountUp, SectionHeader } from "@/components/ui-kit";
import { useSeo } from "@/hooks/useSeo";

const CTAS = [
  { to: "/donate", label: "Donate Now", icon: HeartHandshake, primary: true, tid: "cta-donate" },
  { to: "/wishlist", label: "Donate an Item", icon: Package, tid: "cta-donate-item" },
  { to: "/veterans/apply", label: "Veteran Application", icon: ShieldCheck, tid: "cta-veteran" },
  { to: "/trainer/apply", label: "Become a Trainer", icon: GraduationCap, tid: "cta-trainer" },
  { to: "/volunteer", label: "Volunteer", icon: Users, tid: "cta-volunteer" },
  { to: "/14ddv", label: "Sponsor 14DDV", icon: Flame, tid: "cta-14ddv" },
];

function Counter({ value, label, prefix = "", suffix = "" }) {
  const [n, ref] = useCountUp(value);
  return (
    <div ref={ref} className="text-center px-3 py-6" data-testid={`counter-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="font-heading text-3xl sm:text-4xl font-bold text-[#D4AF37]">{prefix}{n.toLocaleString()}{suffix}</div>
      <div className="text-xs sm:text-sm text-[#8A877B] mt-2 uppercase tracking-wider">{label}</div>
    </div>
  );
}

export default function Home() {
  useSeo("Strength. Service. Purpose.", "Golden Wolf Foundation connects veterans, service dogs, master trainers, volunteers and donors in Chicago. Donate, apply, or volunteer today.");
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/impact").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const counters = [
    { key: "veterans_served", label: "Veterans Served" },
    { key: "dogs_in_training", label: "Dogs In Training" },
    { key: "trainers_recruited", label: "Trainers Recruited" },
    { key: "volunteer_hours", label: "Volunteer Hours" },
    { key: "donations_received", label: "Donations Received", prefix: "$" },
    { key: "items_still_needed", label: "Items Still Needed" },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center grain overflow-hidden">
        <img src={MEDIA.hero} alt="Golden Wolf founder standing with two large working dogs" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <div className="overline mb-5">{TAGLINE}</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#FDFBF7]">
              Building Service Dogs.<br />Building Master Trainers.<br /><span className="text-[#D4AF37]">Rebuilding Purpose.</span>
            </h1>
            <p className="mt-6 text-lg text-[#C8C4B7] max-w-xl leading-relaxed">{SECONDARY}</p>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
              {CTAS.map((c, i) => (
                <motion.div key={c.to} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.07 }}>
                  <Link to={c.to} data-testid={c.tid}
                    className={`group flex items-center gap-2 px-4 py-3.5 text-sm font-semibold transition-colors ${c.primary ? "btn-gold" : "border border-white/20 text-[#FDFBF7] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
                    <c.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{c.label}</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* IMPACT COUNTERS */}
      <section className="bg-[#141414] border-y border-white/10">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-white/5">
          {counters.map((c) => (
            <Counter key={c.key} value={stats ? Math.round(stats[c.key] || 0) : 0} label={c.label} prefix={c.prefix || ""} />
          ))}
        </div>
      </section>

      {/* CAMPAIGN — FIRE */}
      <section className="relative py-28 grain">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Flame className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
          <h2 className="font-heading text-4xl sm:text-6xl font-bold text-[#FDFBF7] leading-tight">"{CAMPAIGN_TAGLINE}"</h2>
          <p className="mt-6 text-lg text-[#C8C4B7] max-w-2xl mx-auto leading-relaxed">
            Every veteran carries a fire that never went out. We give it direction — through the discipline of training a dog, the purpose of a mission, and the dignity of building something that lasts.
          </p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to="/donate" className="btn-gold" data-testid="fire-donate">Fuel the Mission</Link>
            <Link to="/mission" className="btn-outline" data-testid="fire-mission">Our Mission</Link>
          </div>
        </div>
      </section>

      {/* PROGRAMS BENTO */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <SectionHeader overline="What We Build" title="Three programs. One transformation." />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <ProgramCard className="md:col-span-8" img={MEDIA.veteranDog} icon={Dog} title="Service Dog Program"
            desc="We pair veterans with professionally trained working dogs and support the full journey — from screening to placement to lifelong partnership." to="/service-dog-program" />
          <ProgramCard className="md:col-span-4" img={MEDIA.trainer} icon={GraduationCap} title="Master Dog Trainer Program"
            desc="Veterans and civilians become certified professional trainers through a structured apprenticeship." to="/trainer-program" />
          <ProgramCard className="md:col-span-4" img={MEDIA.mural} icon={Award} title="Golden Wolf Campus & Art"
            desc="A healing campus in Chicago, built with community hands and covered in murals by local artists." to="/artists" />
          <ProgramCard className="md:col-span-8" img={MEDIA.dog1} icon={Flame} title="14DDV — 14 Dogs for Disabled Veterans"
            desc="Labor Day Weekend 2026. Fourteen dogs. Four days. One mission. Sponsor a dog, register, or volunteer." to="/14ddv" />
        </div>
      </section>

      {/* WISHLIST TEASER */}
      <section className="bg-[#141414] border-y border-white/10 py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-3">In-Kind Wish List</div>
            <h2 className="font-heading text-4xl font-semibold text-[#FDFBF7]">Give what we need. Build what lasts.</h2>
            <p className="mt-4 text-[#C8C4B7] leading-relaxed">From dog crates to patio lumber to skilled labor — every donated item builds the campus and equips our dogs. Browse the live wish list and claim an item to donate.</p>
            <Link to="/wishlist" className="btn-gold mt-8 inline-flex items-center gap-2" data-testid="home-wishlist-btn">Browse the Wish List <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={MEDIA.gear} alt="Training equipment and gear" className="w-full h-48 object-cover" />
            <img src={MEDIA.food} alt="Dog food and supplies" className="w-full h-48 object-cover mt-8" />
          </div>
        </div>
      </section>
    </div>
  );
}

function ProgramCard({ className, img, icon: Icon, title, desc, to }) {
  return (
    <Link to={to} className={`gw-card group relative overflow-hidden min-h-[280px] flex flex-col justify-end ${className}`} data-testid={`program-${to.slice(1)}`}>
      <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
      <div className="relative p-7">
        <Icon className="w-7 h-7 text-[#D4AF37] mb-3" />
        <h3 className="font-heading text-2xl font-semibold text-[#FDFBF7]">{title}</h3>
        <p className="mt-2 text-sm text-[#C8C4B7] max-w-md leading-relaxed">{desc}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#D4AF37]">Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
      </div>
    </Link>
  );
}
